import { hasValue } from "../../../util";
import { ICollection, IModelAction, ITargetDataStructureOptions, TargetDataStructure } from "./datasource-types";

/* safe default schema */
export const DEFAULT_SCHEMA: any = { type: 'array', items: { type: 'object', properties: {}, required: [] } };

export function isModelCollection(t: ICollection | IModelAction): t is ICollection {
    return (t as any)?.Type == 'collection';
}
export function isExternalDatabase(t: ICollection | IModelAction): t is ICollection {
    return (t as any)?.Type == 'external';
}


function isScalar(t: string) {
    return t == 'string' || t == 'number' || t == 'integer';
}

function deriveJSONPathForValue(schema: any, options: ITargetDataStructureOptions, existingPath: string = ''): string {
    switch (schema.type) {
        case 'string':
        case 'number':
        case 'integer':
            return existingPath;
        case 'array':
            let items = schema.items;
            if (items instanceof Array) {
                throw 'Array invalid type structure';
            }
            if (typeof items == 'object') {
                let subSchema = items;
                return deriveJSONPathForValue(subSchema, options, existingPath + '.[0]');
            }
            throw 'Array contains unknown items element:' + JSON.stringify(items);
        case 'object':
            let props = schema.properties;
            let propNames = Object.keys(props);
            if (propNames.length == 0) throw 'Object with no properties:' + JSON.stringify(schema);
            let PROPNAMES = propNames.map(x => x.toUpperCase());
            let PREFERREDNAMES = options?.preferredFields?.map(x => x.toUpperCase());
            for (let pn in PREFERREDNAMES) {
                let pi = PROPNAMES.indexOf(pn);
                if (pi < 0) continue;
                let propName = propNames[pi];
                if (isScalar(props[propName].type)) {
                    return existingPath + '.' + propName;
                }
            }
            let firstProp = propNames.find(x => isScalar(props[x].type));
            if (firstProp) {
                return existingPath + '.' + firstProp;
            }

            let firstArrayProp = propNames.find(x => props[x].type == 'array');
            return deriveJSONPathForValue(props[firstArrayProp], options, existingPath + '.' + firstArrayProp);
    }
    throw 'Should not be here';

}

function deriveJSONPathForDictionary(schema: any, options: ITargetDataStructureOptions, existingPath: string = '') {
    if (schema.type == 'object') {
        return existingPath;
    }
    if (schema.type == 'array') {
        let items = schema.items;
        if (items instanceof Array) {
            throw 'Nested arrays found:' + JSON.stringify(schema);
        }

        return existingPath + '.[0]';
    }
    throw 'No dictionary found: ' + JSON.stringify(schema);

}

function deriveJSONPathForDictionaryArray(schema: any, options: ITargetDataStructureOptions, existingPath: string): string {
    if (schema.type == 'object') {
        let props = schema.properties;
        for (let k in props) {
            let v = props[k];
            if (v.type == 'array') {
                return deriveJSONPathForDictionaryArray(v, options, existingPath + '.' + k);
            }
        }
        throw 'No array found in dictionary:' + JSON.stringify(schema);
    }
    if (schema.type == 'array') {
        let items = schema.items;
        if (items instanceof Array) {
            throw 'Nested array list found:' + JSON.stringify(schema);
        }
        if (items.type == 'object') {
            return existingPath;
        }
    }
    throw 'Unable to find json path for dict array:' + JSON.stringify(schema);
}

export function deriveJSONPath(schema: any, options: ITargetDataStructureOptions, target: TargetDataStructure) {
    switch (target) {
        case 'value': return deriveJSONPathForValue(schema, options, '$');
        case 'dictionary': return deriveJSONPathForDictionary(schema, options, '$');
        case 'dictionary-array': return deriveJSONPathForDictionaryArray(schema, options, '$');
        case 'any':
        case null:
            try {
                return deriveJSONPathForDictionaryArray(schema, options, '$');
            } catch (e) {
                console.log('Error extracting dict array schema');
            }
            try {
                return deriveJSONPathForDictionary(schema, options, '$');
            } catch (e) {
                console.log('Error extracting dict schema');
            }
            try {
                return deriveJSONPathForValue(schema, options, '$');
            } catch (e) {
                console.log('Error extracting value schema');
            }
            throw 'Unable to match "any" schema';
    }
}

export function validateSchema(schema: any, target: TargetDataStructure) {
    switch (target) {
        case "dictionary": return schema.type == 'object';
        case 'dictionary-array': return (schema.type == 'array') && (schema.items?.type == 'object');
        case "value": return ['string', 'number', 'integer'].indexOf(schema.type) >= 0;
        case 'any': return true;
        case null: return true
    }
    return false;
}

export function schemaAtJsonPath(schema: any, path: string): any {
    if (!schema) {
        return schema;
    }
    if (!hasValue(path)) return schema;

    let [first, ..._rest] = path.split('.');
    let rest = _rest.join('.');
    if (first == '$') {
        return schemaAtJsonPath(schema, rest);
    }
    let indexMatches = first.match(/\[(\d+)\]/);
    if (indexMatches) {
        let index = Number(indexMatches[1]);
        if (schema.type != 'array') {
            throw 'Expected array in schema: ' + JSON.stringify(schema) + ":" + path;
        }
        //TODO: what to do with items is empty?
        let items = schema.items || { type: "object", "properties": {} };

        if (items.type == 'array') {
            throw 'Unexpected array structure in schema: ' + JSON.stringify(schema);
        }
        return schemaAtJsonPath(items, rest);

    }
    if (schema.type != 'object') {
        throw 'Expected object in schema: ' + JSON.stringify(schema) + ":" + path;
    }
    if (!schema?.properties?.[first]) {
        throw 'Unable to find key ' + first + ' in object ' + JSON.stringify(schema);
    }
    return schemaAtJsonPath(schema.properties[first], rest);

}

export function combinePaths(path: string, ...rest: string[]) {
    let finalPath = path;
    for (let p of rest) {
        if (!!p) {
            finalPath = finalPath + '.' + p.replace(/^\$\.?/, '');
            if (finalPath.endsWith('.')) {
                finalPath = finalPath.slice(0, -1);
            }
        }
    }
    return finalPath;
}
